// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FormNumberTextField } from '@/components/form-number-text-field';
import { FormTextField } from '@/components/form-text-field';
import {
  ArrowForwardOutlined,
  CheckOutlined,
  DeleteRounded,
} from '@/constants/hardfin-standard-icons';
import { ItemSearch } from '@/items/components/item-search';
import { ReceiveDialogFormData } from '@/shipment/components/receive-dialog';
import { shipmentLineAllowsSerials } from '@/shipment/utilities/shipment-serials';
import { SparseItem } from '@/sparse/api/types';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import {
  Box,
  Chip,
  IconButton,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Control,
  Controller,
  FieldError,
  UseFormStateReturn,
} from 'react-hook-form';

interface Props {
  index: number;
  item: ReceiveDialogFormData['items'][number];
  watchedItem: ReceiveDialogFormData['items'][number] | undefined;
  control: Control<ReceiveDialogFormData>;
  formState: UseFormStateReturn<ReceiveDialogFormData>;
  hiddenItems: SparseItem[];
  onRemove: (index: number) => void;
  onSerialDrawerOpen: (index: number | null) => void;
  serialDrawerOpen: number | null;
  onClearErrors: (index: number) => void;
  invalidSerials?: Set<string>;
}

export function ReceiveDialogNewItem(props: Props) {
  const shouldRenderSerialFields = shipmentLineAllowsSerials(
    props.watchedItem?.item
  );

  const invalidSerialCount = props.invalidSerials?.size ?? 0;
  const itemError = props.formState.errors.items?.[props.index] as
    | { serials?: FieldError }
    | undefined;
  const hasFormError = !!itemError?.serials;
  const hasError = invalidSerialCount > 0 || hasFormError;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderColor: hasError ? 'error.main' : 'divider',
        borderWidth: hasError ? 2 : 1,
      }}
    >
      <Stack gap={2}>
        <Stack direction="row" gap={3}>
          {/* Left column - Item selector (60%) */}
          <Stack gap={1} sx={{ width: '60%' }}>
            <Controller
              control={props.control}
              name={`items.${props.index}`}
              render={({ field: itemField, fieldState }) => {
                const currentValue = itemField.value;
                // The item in the form is already a SparseItem
                const sparseItemValue = currentValue?.item ?? null;

                return (
                  <ItemSearch
                    label="Item"
                    value={sparseItemValue}
                    physicalOnly={true}
                    onChange={(value) => {
                      itemField.onChange({
                        item: value,
                        quantity: currentValue?.quantity ?? 1,
                        initialQuantity: currentValue?.initialQuantity ?? null,
                        externalReference:
                          currentValue?.externalReference ?? null,
                        serials: [],
                        isOriginalItem: false,
                      });
                    }}
                    hiddenItems={props.hiddenItems}
                    error={
                      !!(
                        fieldState.error as {
                          item?: { message?: string };
                        }
                      )?.item
                    }
                    helperText={
                      (
                        fieldState.error as {
                          item?: { message?: string };
                        }
                      )?.item?.message
                    }
                  />
                );
              }}
            />
          </Stack>
          {/* Right column - Delete button and Received (40%) */}
          <Stack gap={1} sx={{ width: '40%' }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              {shouldRenderSerialFields && (
                <Chip
                  label="serialized"
                  size="small"
                  sx={{
                    height: '20px',
                    fontSize: '0.75rem',
                    mt: 1,
                  }}
                />
              )}
              <IconButton
                size="small"
                onClick={() => props.onRemove(props.index)}
                sx={{ ml: 'auto' }}
              >
                <DeleteRounded fontSize="small" />
              </IconButton>
            </Stack>
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ width: '70px' }}
              >
                Received
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  '& input': {
                    textAlign: 'right',
                  },
                }}
              >
                <FormNumberTextField
                  nullable
                  numeric
                  control={props.control}
                  name={`items.${props.index}.quantity`}
                  onChange={() => props.onClearErrors(props.index)}
                />
              </Box>
            </Stack>
          </Stack>
        </Stack>
        <Stack direction="row" gap={3} alignItems="center">
          <Box sx={{ width: '60%' }}>
            <Box sx={{ width: '70%' }}>
              <FormTextField
                fullWidth
                label="Reference ID (optional)"
                placeholder="Ex: batch number"
                control={props.control}
                name={`items.${props.index}.externalReference`}
              />
            </Box>
          </Box>
          <Box sx={{ width: '40%' }}>
            {shouldRenderSerialFields &&
              (() => {
                const serialCount = props.watchedItem?.serials?.length || 0;
                const expectedQuantity = props.watchedItem?.quantity || 0;
                const localItemError = props.formState.errors.items?.[
                  props.index
                ] as { serials?: FieldError } | undefined;
                const hasError = !!localItemError?.serials;

                let text = '';
                let icon = null;
                let color = 'text.secondary';

                if (invalidSerialCount > 0) {
                  // Show invalid serials count
                  text = `${invalidSerialCount} out of ${serialCount} ${pluralize(
                    serialCount,
                    {
                      singular: 'serial',
                      plural: 'serials',
                      includeCount: false,
                    }
                  )} ${invalidSerialCount === 1 ? 'is' : 'are'} invalid`;
                  color = 'error.main';
                } else if (serialCount === 0 && expectedQuantity === 0) {
                  text = 'Complete';
                  icon = <CheckOutlined fontSize="small" />;
                  color = 'success.main';
                } else if (serialCount === 0) {
                  text = 'No serials entered';
                  icon = <ArrowForwardOutlined fontSize="small" />;
                  color = hasError ? 'error.main' : 'text.secondary';
                } else if (serialCount === expectedQuantity) {
                  text = `${formatNumber(serialCount)} serials entered`;
                  icon = <CheckOutlined fontSize="small" />;
                  color = 'success.main';
                } else if (serialCount > expectedQuantity) {
                  const extra = serialCount - expectedQuantity;
                  text = `${pluralize(serialCount, {
                    singular: 'serial',
                    plural: 'serials',
                    includeCount: true,
                  })} entered (${extra} extra)`;
                  color = 'error.main';
                } else {
                  const needed = expectedQuantity - serialCount;
                  text = `${pluralize(serialCount, {
                    singular: 'serial',
                    plural: 'serials',
                    includeCount: true,
                  })} entered (${needed} needed)`;
                  color = hasError ? 'error.main' : 'warning.dark';
                }

                return (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() =>
                      props.onSerialDrawerOpen(
                        props.serialDrawerOpen === props.index
                          ? null
                          : props.index
                      )
                    }
                    sx={{
                      textAlign: 'right',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.5,
                      width: '100%',
                      cursor: 'pointer',
                      color: color,
                    }}
                  >
                    {text}
                    {icon}
                  </Link>
                );
              })()}
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
}
